export type ClientCredentialObservationState = "healthy" | "reconnect_required";

export type ClientCredentialObservation = {
  connectionId: string;
  provider: string;
  state: ClientCredentialObservationState;
  reason: string;
  observedAt: number;
  statusCode?: number;
};

type ObservationInput = Omit<ClientCredentialObservation, "state" | "observedAt"> & {
  observedAt?: number;
};

type Listener = () => void;

let observations = new Map<string, ClientCredentialObservation>();
let snapshot: readonly ClientCredentialObservation[] = [];
const listeners = new Set<Listener>();

function publish(next: Map<string, ClientCredentialObservation>): void {
  observations = next;
  snapshot = [...next.values()];
  for (const listener of listeners) listener();
}

function observe(input: ObservationInput, state: ClientCredentialObservationState): void {
  const next = new Map(observations);
  next.set(input.connectionId, {
    ...input,
    state,
    observedAt: input.observedAt ?? Date.now(),
  });
  publish(next);
}

export function observeClientCredentialFailure(input: ObservationInput): void {
  observe(input, "reconnect_required");
}

export function observeClientCredentialRecovery(input: ObservationInput): void {
  observe(input, "healthy");
}

export function getClientCredentialObservation(
  connectionId: string | null | undefined
): ClientCredentialObservation | null {
  return connectionId ? (observations.get(connectionId) ?? null) : null;
}

export function hasClientCredentialFailure(connectionId: string | null | undefined): boolean {
  return getClientCredentialObservation(connectionId)?.state === "reconnect_required";
}

export function clearClientCredentialObservation(connectionId: string | null | undefined): void {
  if (!connectionId || !observations.has(connectionId)) return;
  const next = new Map(observations);
  next.delete(connectionId);
  publish(next);
}

export function clearClientCredentialObservations(): void {
  if (observations.size === 0) return;
  publish(new Map());
}

export function retainClientCredentialObservations(connectionIds: ReadonlySet<string>): void {
  const next = new Map(
    [...observations].filter(([connectionId]) => connectionIds.has(connectionId))
  );
  if (next.size === observations.size) return;
  publish(next);
}

export function subscribeClientCredentialObservations(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getClientCredentialObservationsSnapshot(): readonly ClientCredentialObservation[] {
  return snapshot;
}
